import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

import {
  getHospitals,
  getWards,
  getBeds,
  getDevices,
  getAdmissions,
  getDeviceAssignments,
  createDeviceAssignment,
} from "../api/deviceAssignmentsApi";
import { prettyError } from "../utils/prettyError";
import { Colors } from "../../utils/Colors";

const initialForm = {
  hospital: "",
  ward: "",
  bed_id: "",
  bed_label: "",
  device_id: "",
  admission_id: "",
  patient_name: "",
  patient_uhid: "",
  assigned_at: "",
  unassigned_at: "",
};

export default function DeviceAssignmentForm() {
  const [form, setForm] = React.useState(initialForm);

  const [hospitals, setHospitals] = React.useState([]);
  const [wards, setWards] = React.useState([]);
  const [beds, setBeds] = React.useState([]);
  const [devices, setDevices] = React.useState([]);
  const [admissions, setAdmissions] = React.useState([]);
  const [assignments, setAssignments] = React.useState([]);

  const [loadingData, setLoadingData] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [success, setSuccess] = React.useState("");
  const [error, setError] = React.useState("");
  const [createdAssignment, setCreatedAssignment] = React.useState(null);

  React.useEffect(() => {
    loadBootstrapData();
  }, []);

  function normalizeArray(response) {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.results)) return response.results;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.items)) return response.items;
    return [];
  }

  const loadBootstrapData = async () => {
    try {
      setLoadingData(true);
      setError("");

      const [
        hospitalRes,
        wardRes,
        bedRes,
        deviceRes,
        admissionRes,
        assignmentRes,
      ] = await Promise.all([
        getHospitals(),
        getWards(),
        getBeds(),
        getDevices(),
        getAdmissions(),
        getDeviceAssignments(),
      ]);

      setHospitals(normalizeArray(hospitalRes));
      setWards(normalizeArray(wardRes));
      setBeds(normalizeArray(bedRes));
      setDevices(normalizeArray(deviceRes));
      setAdmissions(normalizeArray(admissionRes));
      setAssignments(normalizeArray(assignmentRes));
    } catch (err) {
      setError(prettyError(err));
      setHospitals([]);
      setWards([]);
      setBeds([]);
      setDevices([]);
      setAdmissions([]);
      setAssignments([]);
    } finally {
      setLoadingData(false);
    }
  };

  const refreshAssignmentsOnly = async () => {
    const res = await getDeviceAssignments();
    const rows = normalizeArray(res);
    setAssignments(rows);
    return rows;
  };

  const asString = (value) => {
    if (value === null || value === undefined) return "";
    return String(value);
  };

  const normalizeText = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const getHospitalId = (item) =>
    item?.organization_id ??
    item?.hospital_id ??
    item?.id ??
    item?.uuid ??
    "";

  const getWardId = (item) =>
    item?.ward_id ??
    item?.id ??
    item?.uuid ??
    "";

  const getBedId = (item) =>
    item?.bed_id ??
    item?.id ??
    item?.uuid ??
    "";

  const getDeviceId = (item) =>
    item?.device_id ??
    item?.id ??
    item?.uuid ??
    "";

  const getAdmissionId = (item) =>
    item?.admission_id ??
    item?.id ??
    item?.uuid ??
    "";

  const getAssignmentDeviceId = (item) =>
    item?.device?.device_id ??
    item?.device?.id ??
    item?.device_id ??
    item?.device ??
    "";

  const getAssignmentUnassignedAt = (item) =>
    item?.unassigned_at ??
    item?.released_at ??
    item?.ended_at ??
    "";

  const getWardHospitalId = (item) =>
    item?.organization?.organization_id ??
    item?.organization?.id ??
    item?.organization_id ??
    item?.organization ??
    item?.hospital?.hospital_id ??
    item?.hospital?.id ??
    item?.hospital_id ??
    item?.hospital ??
    "";

  const getBedWardId = (item) =>
    item?.ward?.ward_id ??
    item?.ward?.id ??
    item?.ward_id ??
    item?.ward ??
    item?.ward_unit?.ward_id ??
    item?.ward_unit?.id ??
    item?.ward_unit_id ??
    item?.ward_unit ??
    "";

  const getBedHospitalId = (item) =>
    item?.organization?.organization_id ??
    item?.organization?.id ??
    item?.organization_id ??
    item?.organization ??
    item?.hospital?.hospital_id ??
    item?.hospital?.id ??
    item?.hospital_id ??
    item?.hospital ??
    "";

  const getAdmissionHospitalId = (item) =>
    item?.organization?.organization_id ??
    item?.organization?.id ??
    item?.organization_id ??
    item?.organization ??
    item?.hospital?.hospital_id ??
    item?.hospital?.id ??
    item?.hospital_id ??
    item?.hospital ??
    "";

  const getAdmissionBedId = (item) =>
    item?.bed?.bed_id ??
    item?.bed?.id ??
    item?.bed_id ??
    item?.bed ??
    "";

  const getHospitalLabel = (item) =>
    item?.name ||
    item?.organization_name ||
    item?.hospital_name ||
    item?.title ||
    item?.label ||
    "Unnamed Hospital";

  const getWardLabel = (item) =>
    item?.ward_name ||
    item?.ward_unit ||
    item?.ward_type ||
    item?.name ||
    item?.label ||
    item?.display_name ||
    item?.title ||
    "Unnamed Ward";

  const getBedLabel = (item) =>
    item?.bed_number ||
    item?.bed_no ||
    item?.number ||
    item?.bed_name ||
    item?.name ||
    item?.label ||
    item?.display_name ||
    item?.title ||
    "Unnamed Bed";

  const getDeviceLabel = (item) => {
    const serial =
      item?.device_serial ||
      item?.serial_number ||
      item?.serial ||
      item?.device_code ||
      "";

    const model =
      item?.device_model ||
      item?.model ||
      item?.device_name ||
      item?.name ||
      "";

    if (serial && model) return `${serial} - ${model}`;
    if (serial) return serial;
    if (model) return model;
    return "Unnamed Device";
  };

  const getAdmissionNo = (item) =>
    item?.admission_no ||
    item?.admission_number ||
    item?.admission_code ||
    "";

  const getAdmissionPatientName = (item) =>
    item?.patient_name ||
    item?.patient?.full_name ||
    item?.patient?.name ||
    item?.full_name ||
    "";

  const getAdmissionPatientUhid = (item) =>
    item?.patient_uhid_out ||
    item?.patient_uhid ||
    item?.uhid ||
    item?.uhid_no ||
    item?.uhid_number ||
    item?.patient?.uhid ||
    "";

  const getAdmissionHospitalName = (item) =>
    item?.organization_name ||
    item?.hospital_name ||
    item?.hospital ||
    "";

  const getAdmissionWardName = (item) =>
    item?.ward_unit ||
    item?.ward_name ||
    item?.ward ||
    "";

  const getAdmissionBedName = (item) =>
    item?.bed_number ||
    item?.bed_name ||
    item?.bed ||
    "";

  const getAdmissionLabel = (item) => {
    const admissionNo = getAdmissionNo(item);
    const patientName = getAdmissionPatientName(item);

    if (admissionNo && patientName) return `${admissionNo} - ${patientName}`;
    if (admissionNo) return admissionNo;
    if (patientName) return patientName;
    return "Unnamed Admission";
  };

  const isAssignmentActive = React.useCallback((assignment) => {
    const unassignedAt = getAssignmentUnassignedAt(assignment);

    if (unassignedAt === null || unassignedAt === undefined) return true;
    if (String(unassignedAt).trim() === "") return true;

    return false;
  }, []);

  const assignedDeviceIdSet = React.useMemo(() => {
    const activeAssignments = assignments.filter(isAssignmentActive);

    return new Set(
      activeAssignments
        .map((item) => asString(getAssignmentDeviceId(item)))
        .filter(Boolean)
    );
  }, [assignments, isAssignmentActive]);

  const availableDevices = React.useMemo(() => {
    return devices.filter((item) => {
      const deviceId = asString(getDeviceId(item));
      return deviceId && !assignedDeviceIdSet.has(deviceId);
    });
  }, [devices, assignedDeviceIdSet]);

  const isSelectedDeviceAvailable = React.useMemo(() => {
    if (!form.device_id) return true;
    return availableDevices.some(
      (item) => asString(getDeviceId(item)) === asString(form.device_id)
    );
  }, [availableDevices, form.device_id]);

  const findHospitalByAdmission = React.useCallback(
    (admission) => {
      if (!admission) return null;

      const directId = getAdmissionHospitalId(admission);
      if (directId) {
        const byId = hospitals.find(
          (item) => asString(getHospitalId(item)) === asString(directId)
        );
        if (byId) return byId;
      }

      const hospitalName = normalizeText(getAdmissionHospitalName(admission));
      if (!hospitalName) return null;

      return (
        hospitals.find(
          (item) => normalizeText(getHospitalLabel(item)) === hospitalName
        ) || null
      );
    },
    [hospitals]
  );

  const findWardByAdmission = React.useCallback(
    (admission, hospitalObj) => {
      if (!admission) return null;

      const admissionWard = normalizeText(getAdmissionWardName(admission));
      if (!admissionWard) return null;

      const hospitalId = hospitalObj ? asString(getHospitalId(hospitalObj)) : "";

      return (
        wards.find((item) => {
          const wardName = normalizeText(getWardLabel(item));
          const wardHospitalId = asString(getWardHospitalId(item));
          const hospitalMatches = !hospitalId || wardHospitalId === hospitalId;
          return hospitalMatches && wardName === admissionWard;
        }) || null
      );
    },
    [wards]
  );

  const findBedByAdmission = React.useCallback(
    (admission, wardObj, hospitalObj) => {
      if (!admission) return null;

      const directBedId = getAdmissionBedId(admission);
      if (directBedId) {
        const byId = beds.find(
          (item) => asString(getBedId(item)) === asString(directBedId)
        );
        if (byId) return byId;
      }

      const admissionBed = normalizeText(getAdmissionBedName(admission));
      if (!admissionBed) return null;

      const wardId = wardObj ? asString(getWardId(wardObj)) : "";
      const hospitalId = hospitalObj ? asString(getHospitalId(hospitalObj)) : "";

      return (
        beds.find((item) => {
          const bedName = normalizeText(getBedLabel(item));
          if (bedName !== admissionBed) return false;

          const bedWardId = asString(getBedWardId(item));
          const bedHospitalId = asString(getBedHospitalId(item));

          const wardMatches = !wardId || bedWardId === wardId || !bedWardId;
          const hospitalMatches =
            !hospitalId || bedHospitalId === hospitalId || !bedHospitalId;

          return wardMatches && hospitalMatches;
        }) || null
      );
    },
    [beds]
  );

  const handleAdmissionChange = (event) => {
    const admissionId = event.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        admission_id: admissionId,
      };

      const selected = admissions.find(
        (item) => asString(getAdmissionId(item)) === asString(admissionId)
      );

      if (!selected) {
        next.hospital = "";
        next.ward = "";
        next.bed_id = "";
        next.bed_label = "";
        next.patient_name = "";
        next.patient_uhid = "";
        return next;
      }

      const hospitalObj = findHospitalByAdmission(selected);
      const wardObj = findWardByAdmission(selected, hospitalObj);
      const bedObj = findBedByAdmission(selected, wardObj, hospitalObj);

      next.hospital = hospitalObj ? asString(getHospitalId(hospitalObj)) : "";
      next.ward = wardObj ? asString(getWardId(wardObj)) : "";
      next.bed_id = bedObj ? asString(getBedId(bedObj)) : "";
      next.bed_label = getAdmissionBedName(selected) || "";
      next.patient_name = getAdmissionPatientName(selected) || "";
      next.patient_uhid = getAdmissionPatientUhid(selected) || "";

      return next;
    });
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const filteredWards = React.useMemo(() => {
    if (!form.hospital) return [];
    return wards.filter((item) => {
      const parentId = getWardHospitalId(item);
      return asString(parentId) === asString(form.hospital);
    });
  }, [wards, form.hospital]);

  const selectedHospital = React.useMemo(
    () =>
      hospitals.find(
        (item) => asString(getHospitalId(item)) === asString(form.hospital)
      ) || null,
    [hospitals, form.hospital]
  );

  const selectedWard = React.useMemo(
    () =>
      wards.find((item) => asString(getWardId(item)) === asString(form.ward)) ||
      null,
    [wards, form.ward]
  );

  const selectedBed = React.useMemo(
    () =>
      beds.find((item) => asString(getBedId(item)) === asString(form.bed_id)) ||
      null,
    [beds, form.bed_id]
  );

  const selectedDevice = React.useMemo(
    () =>
      devices.find(
        (item) => asString(getDeviceId(item)) === asString(form.device_id)
      ) || null,
    [devices, form.device_id]
  );

  const selectedAdmission = React.useMemo(
    () =>
      admissions.find(
        (item) => asString(getAdmissionId(item)) === asString(form.admission_id)
      ) || null,
    [admissions, form.admission_id]
  );

  const validate = () => {
    if (!form.device_id) return "Device serial is required.";
    if (!isSelectedDeviceAvailable) {
      return "Selected device is already assigned to a patient.";
    }
    if (!form.admission_id) return "Admission number is required.";
    if (!form.bed_id && !form.bed_label) {
      return "Bed number could not be resolved from admission.";
    }
    if (!form.assigned_at) return "Assigned at is required.";

    if (form.unassigned_at && form.assigned_at) {
      const assignedDate = new Date(form.assigned_at);
      const unassignedDate = new Date(form.unassigned_at);

      if (unassignedDate < assignedDate) {
        return "Unassigned at cannot be earlier than assigned at.";
      }
    }

    return "";
  };

  const buildPayload = () => {
    return {
      device_id: form.device_id,
      admission_id: form.admission_id || null,
      bed_id: form.bed_id || null,
      assigned_at: form.assigned_at,
      unassigned_at: form.unassigned_at || null,
      assigned_by_user_id: null,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");
    setCreatedAssignment(null);

    const validationMessage = validate();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setSaving(true);

      const latestAssignments = await refreshAssignmentsOnly();

      const latestAssignedDeviceSet = new Set(
        latestAssignments
          .filter(isAssignmentActive)
          .map((item) => asString(getAssignmentDeviceId(item)))
          .filter(Boolean)
      );

      if (latestAssignedDeviceSet.has(asString(form.device_id))) {
        setError(
          "This device is already assigned. Please select another free device."
        );
        setSaving(false);
        return;
      }

      const payload = buildPayload();
      const response = await createDeviceAssignment(payload);

      setCreatedAssignment({
        ...response,
        hospital_name: selectedHospital ? getHospitalLabel(selectedHospital) : "-",
        ward_name: selectedWard ? getWardLabel(selectedWard) : "-",
        bed_name: form.bed_label || (selectedBed ? getBedLabel(selectedBed) : "-"),
        device_name: selectedDevice ? getDeviceLabel(selectedDevice) : "-",
        admission_name: selectedAdmission ? getAdmissionLabel(selectedAdmission) : "-",
        patient_name: form.patient_name || "-",
        patient_uhid: form.patient_uhid || "-",
      });

      setSuccess("Device assignment created successfully.");
      setForm(initialForm);

      await loadBootstrapData();
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setSuccess("");
    setError("");
    setCreatedAssignment(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: Colors.bg.app,
        minHeight: "100vh",
        py: 3,
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1000, mx: "auto" }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${Colors.border.light}`,
            overflow: "hidden",
            boxShadow: "0 8px 18px rgba(11,60,63,0.08)",
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(90deg, ${Colors.brand.primary} 0%, ${Colors.brand.primaryDark} 100%)`,
              px: 3,
              py: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
            >
              Device Assignment
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.88)",
                mt: 0.5,
                fontSize: "0.82rem",
              }}
            >
              Select device and admission. Patient name, UHID, hospital, ward,
              and bed fill automatically from admission.
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: Colors.text.header,
                mb: 0.5,
              }}
            >
              Assignment Details
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: Colors.text.muted,
                mb: 2,
              }}
            >
              Admission is the reference source.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <FormRow
                  label="Device Serial"
                  field={
                    <TextField
                      fullWidth
                      select
                      value={form.device_id}
                      onChange={handleChange("device_id")}
                      disabled={loadingData}
                      error={Boolean(form.device_id && !isSelectedDeviceAvailable)}
                      helperText={
                        form.device_id && !isSelectedDeviceAvailable
                          ? "This device is already assigned."
                          : availableDevices.length === 0 && !loadingData
                          ? "No free devices available."
                          : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MemoryOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="">
                        {loadingData
                          ? "Loading devices..."
                          : availableDevices.length === 0
                          ? "No free device available"
                          : "Select device serial"}
                      </MenuItem>

                      {availableDevices.map((item) => (
                        <MenuItem
                          key={asString(getDeviceId(item))}
                          value={asString(getDeviceId(item))}
                        >
                          {getDeviceLabel(item)}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />

                <FormRow
                  label="Admission Number"
                  field={
                    <TextField
                      fullWidth
                      select
                      value={form.admission_id}
                      onChange={handleAdmissionChange}
                      disabled={loadingData}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="">
                        {loadingData ? "Loading admissions..." : "Select admission"}
                      </MenuItem>

                      {admissions.map((item) => (
                        <MenuItem
                          key={asString(getAdmissionId(item))}
                          value={asString(getAdmissionId(item))}
                        >
                          {getAdmissionLabel(item)}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />

                <FormRow
                  label="Patient Name"
                  field={
                    <TextField
                      fullWidth
                      value={form.patient_name}
                      disabled
                      placeholder="Auto filled from admission"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  }
                />

                <FormRow
                  label="UHID"
                  field={
                    <TextField
                      fullWidth
                      value={form.patient_uhid}
                      disabled
                      placeholder="Auto filled from admission"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  }
                />

                <FormRow
                  label="Hospital Name"
                  field={
                    <TextField
                      fullWidth
                      select
                      value={form.hospital}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ApartmentOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="">
                        {form.admission_id ? "Auto selected" : "Select admission first"}
                      </MenuItem>

                      {hospitals.map((item) => (
                        <MenuItem
                          key={asString(getHospitalId(item))}
                          value={asString(getHospitalId(item))}
                        >
                          {getHospitalLabel(item)}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />

                <FormRow
                  label="Ward Name"
                  field={
                    <TextField
                      fullWidth
                      select
                      value={form.ward}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalHospitalOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="">
                        {form.admission_id ? "Auto selected" : "Select admission first"}
                      </MenuItem>

                      {filteredWards.map((item) => (
                        <MenuItem
                          key={asString(getWardId(item))}
                          value={asString(getWardId(item))}
                        >
                          {getWardLabel(item)}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />

                <FormRow
                  label="Bed Number"
                  field={
                    <TextField
                      fullWidth
                      value={form.bed_label}
                      disabled
                      placeholder="Auto filled from admission"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BedOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  }
                />

                <FormRow
                  label="Assigned At"
                  field={
                    <TextField
                      fullWidth
                      type="datetime-local"
                      value={form.assigned_at}
                      onChange={handleChange("assigned_at")}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />

                <FormRow
                  label="Unassigned At"
                  field={
                    <TextField
                      fullWidth
                      type="datetime-local"
                      value={form.unassigned_at}
                      onChange={handleChange("unassigned_at")}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EventBusyOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />

                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${Colors.border.light}`,
                    backgroundColor: "#fff",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 1.5, color: Colors.text.title }}
                    >
                      Selected Preview
                    </Typography>

                    <Stack spacing={1}>
                      <InfoRow
                        label="Admission Number"
                        value={selectedAdmission ? getAdmissionNo(selectedAdmission) : "-"}
                      />
                      <InfoRow label="Patient Name" value={form.patient_name || "-"} />
                      <InfoRow label="UHID" value={form.patient_uhid || "-"} />
                      <InfoRow
                        label="Hospital"
                        value={selectedHospital ? getHospitalLabel(selectedHospital) : "-"}
                      />
                      <InfoRow
                        label="Ward"
                        value={selectedWard ? getWardLabel(selectedWard) : "-"}
                      />
                      <InfoRow
                        label="Bed"
                        value={
                          form.bed_label || (selectedBed ? getBedLabel(selectedBed) : "-")
                        }
                      />
                      <InfoRow
                        label="Device"
                        value={selectedDevice ? getDeviceLabel(selectedDevice) : "-"}
                      />
                    </Stack>
                  </CardContent>
                </Card>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="flex-end"
                  sx={{ pt: 1 }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RestartAltOutlinedIcon />}
                    sx={resetBtnSx}
                  >
                    Reset
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving || loadingData}
                    startIcon={
                      saving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveOutlinedIcon />
                      )
                    }
                    sx={submitBtnSx}
                  >
                    {saving ? "Creating..." : "Assign Device"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {createdAssignment ? (
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: `1px solid ${Colors.border.light}`,
              backgroundColor: Colors.bg.paper,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: Colors.text.title, mb: 1.5 }}
              >
                Created Assignment
              </Typography>

              <Stack spacing={1}>
                <InfoRow
                  label="Assignment ID"
                  value={createdAssignment.device_assignment_id || "-"}
                />
                <InfoRow
                  label="Admission"
                  value={createdAssignment.admission_name || "-"}
                />
                <InfoRow
                  label="Patient Name"
                  value={createdAssignment.patient_name || "-"}
                />
                <InfoRow
                  label="UHID"
                  value={createdAssignment.patient_uhid || "-"}
                />
                <InfoRow
                  label="Hospital"
                  value={createdAssignment.hospital_name || "-"}
                />
                <InfoRow
                  label="Ward"
                  value={createdAssignment.ward_name || "-"}
                />
                <InfoRow
                  label="Bed"
                  value={createdAssignment.bed_name || "-"}
                />
                <InfoRow
                  label="Device"
                  value={createdAssignment.device_name || "-"}
                />
                <InfoRow
                  label="Assigned At"
                  value={createdAssignment.assigned_at || "-"}
                />
                <InfoRow
                  label="Unassigned At"
                  value={createdAssignment.unassigned_at || "-"}
                />
              </Stack>
            </CardContent>
          </Card>
        ) : null}
      </Box>
    </Box>
  );
}

function FormRow({ label, field }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
        gap: 1.5,
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          color: Colors.text.header,
          textAlign: { xs: "left", md: "right" },
          pr: { md: 1 },
          fontSize: "0.95rem",
        }}
      >
        {label}
      </Typography>

      <Box>{field}</Box>
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
        borderBottom: `1px solid ${Colors.border.light}`,
      }}
    >
      <Typography variant="body2" sx={{ color: Colors.text.muted }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: Colors.text.title,
          fontWeight: 700,
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 46,
    borderRadius: 2,
    backgroundColor: "#fff",
    "& fieldset": {
      borderColor: Colors.border.input,
    },
    "&:hover fieldset": {
      borderColor: Colors.brand.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: Colors.brand.primary,
      borderWidth: 1.5,
    },
    "&.Mui-disabled": {
      backgroundColor: "#f8fafb",
    },
  },
  "& .MuiInputBase-input": {
    color: Colors.text.body,
    fontSize: "0.95rem",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
  },
};

const submitBtnSx = {
  minWidth: 180,
  height: 46,
  borderRadius: 2,
  textTransform: "uppercase",
  fontWeight: 800,
  letterSpacing: 0.3,
  backgroundColor: Colors.brand.primary,
  "&:hover": {
    backgroundColor: Colors.brand.primaryDark,
  },
};

const resetBtnSx = {
  minWidth: 140,
  height: 46,
  borderRadius: 2,
  textTransform: "uppercase",
  fontWeight: 800,
  letterSpacing: 0.3,
  borderColor: Colors.border.input,
  color: Colors.brand.primary,
  "&:hover": {
    borderColor: Colors.brand.primary,
    backgroundColor: Colors.bg.hover,
  },
};